import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} gallery - The scrolling media track.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'gallery'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.gallery.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.gallery.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
    this.#openGeneration++;
    this.#pauseAllMedia();
    if (this.refs.dialog.open) {
      this.closeDialog();
      // Gallery replacement inserts the new shortcut after this disconnect callback.
      queueMicrotask(() => this.#restoreFocus());
    }
  }

  #selectedIndex = -1;
  #focusReturnTarget = null;
  #closeEventDispatched = false;
  #openGeneration = 0;

  #pauseAllMedia() {
    this.querySelectorAll('deferred-media').forEach((media) => media.pauseMedia?.());
  }

  #restoreFocus() {
    const mediaGallery = this.closest('media-gallery');
    const productId = mediaGallery?.dataset.productId;
    const replacementGallery = productId
      ? this.ownerDocument.querySelector(`media-gallery[data-product-id="${productId}"]`)
      : mediaGallery;
    const replacementButton = replacementGallery?.querySelector('.product-media-gallery__watch-video');
    const target = this.#focusReturnTarget?.isConnected ? this.#focusReturnTarget : replacementButton;
    if (target?.isConnected) target.focus();
    else {
      const heading = replacementGallery?.closest('.shopify-section')?.querySelector('h1, [data-testid="product-title"]');
      if (heading) {
        const temporaryTabindex = !heading.hasAttribute('tabindex');
        if (temporaryTabindex) heading.setAttribute('tabindex', '-1');
        heading.focus();
        if (temporaryTabindex) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
      }
    }
    this.#focusReturnTarget = null;
  }

  #handleNativeClose = () => {
    this.#openGeneration++;
    this.#pauseAllMedia();
    this.#restoreFocus();
    if (!this.#closeEventDispatched) window.dispatchEvent(new DialogCloseEvent());
    this.#closeEventDispatched = false;
  };

  /** Open a video by Shopify media ID, bypassing image view-transition animation. */
  async openVideo(mediaId, trigger) {
    const index = this.refs.media.findIndex((item) => {
      const media = item.querySelector('[data-media-id]');
      return media?.dataset.mediaId === String(mediaId) && item.matches('.product-media-container--video, .product-media-container--external_video');
    });
    if (index < 0) return;
    this.#openGeneration++;
    this.#focusReturnTarget = trigger;
    const { dialog, media, thumbnails } = this.refs;
    dialog.showModal();
    thumbnails.children[index]?.scrollIntoView({ behavior: 'instant' });
    media[index]?.scrollIntoView({ behavior: 'instant' });
    this.selectThumbnail(index, { behavior: 'instant' });
    await this.#startSelectedVideo(index, this.#openGeneration);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    if (!media[index]) return;
    this.#focusReturnTarget = event.target instanceof Element ? event.target.closest('button') : null;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      this.selectThumbnail(index, { behavior: 'instant' });

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (prefersReducedMotion() || !supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (!this.isConnected || !this.refs.dialog.open) return;
    const generation = this.#openGeneration;
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    if (!this.isConnected || !this.refs.dialog.open || generation !== this.#openGeneration) return;
    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex === this.#selectedIndex) return;
    this.#setSelectedIndex(activeIndex);
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    this.#openGeneration++;
    this.#pauseAllMedia();
    const selected = media[this.#selectedIndex];
    if (prefersReducedMotion() || !selected?.matches('.product-media-container--image') || !supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const candidates = slideshowActive ? mediaGallery.slideshow?.slides : mediaGallery?.media;
    const mediaId = mostVisibleElement.querySelector('[data-media-id]')?.dataset.mediaId;
    const slide = mediaId
      ? candidates?.find((item) => item.querySelector('[data-media-id]')?.dataset.mediaId === mediaId)
      : candidates?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#openGeneration++;
    this.#pauseAllMedia();
    dialog.close();
    this.#closeEventDispatched = true;
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const controlSelector = 'video, input, textarea, select, [contenteditable="true"]';
      const eventFromControl = event.composedPath().some(
        (target) => target instanceof Element && target.matches(controlSelector)
      );
      if (eventFromControl || (event.target instanceof Element && event.target.closest(controlSelector))) return;

      event.preventDefault();
      const isRtl = this.ownerDocument.documentElement.dir === 'rtl';
      const movesForward = (event.key === 'ArrowRight') !== isRtl;
      this.#navigateBy(movesForward ? 1 : -1);
    }
  }

  navigatePrevious() {
    this.#navigateBy(-1);
  }

  navigateNext() {
    this.#navigateBy(1);
  }

  #navigateBy(offset) {
    const thumbnails = Array.from(this.refs.thumbnails.querySelectorAll('button'));
    if (thumbnails.length < 2) return;

    const selectedIndex = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true');
    if (selectedIndex < 0) return;

    const nextIndex = (selectedIndex + offset + thumbnails.length) % thumbnails.length;
    this.handleThumbnailClick(nextIndex);
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
    await this.#startSelectedVideo(index, this.#openGeneration);
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (isNaN(index) || index < 0 || index >= this.refs.media.length) return;
    this.#setSelectedIndex(index);

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === index}`);
    });

    if (targetThumbnail instanceof HTMLElement) scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #setSelectedIndex(index) {
    if (index === this.#selectedIndex) return;
    this.#openGeneration++;
    this.#pauseAllMedia();
    this.#selectedIndex = index;
  }

  async #startSelectedVideo(index, generation) {
    const item = this.refs.media[index];
    if (!item?.matches('.product-media-container--video, .product-media-container--external_video')) return;
    await customElements.whenDefined('deferred-media');
    if (!this.isConnected || !this.refs.dialog.open || generation !== this.#openGeneration || index !== this.#selectedIndex) return;
    await item.querySelector('deferred-media')?.startPlayback();
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
